import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Snackbar, Button, Typography, Box, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';
import { useLanguage } from '../context/LanguageContext';
import { 
  checkForServiceWorkerUpdate, 
  applyServiceWorkerUpdate,
  setupServiceWorkerMessages 
} from '../utils/serviceWorkerHandler';

// Styled components using @mui/material/styles
const UpdateNotification = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: 500,
  boxShadow: theme.shadows[3],
}));

const OfflineNotification = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.warning.dark,
  color: theme.palette.warning.contrastText,
}));

const OnlineNotification = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: theme.palette.success.contrastText,
}));

const UpdateButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  color: theme.palette.primary.contrastText,
  borderColor: theme.palette.primary.contrastText,
}));

const ServiceWorkerUpdater = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [networkStatus, setNetworkStatus] = useState({
    show: false,
    isOnline: navigator.onLine,
    message: '',
  });
  const { language } = useLanguage();
  
  // الرسائل حسب اللغة - استخدام useMemo لتجنب إعادة الإنشاء في كل عملية تحديث
  const messages = React.useMemo(() => ({
    update: {
      ar: 'يتوفر إصدار جديد من التطبيق',
      en: 'A new version of the app is available'
    },
    updateAction: {
      ar: 'تحديث الآن',
      en: 'Update Now'
    },
    online: {
      ar: 'تم استعادة الاتصال بالإنترنت',
      en: 'Internet connection restored'
    },
    offline: {
      ar: 'أنت حاليا غير متصل بالإنترنت',
      en: 'You are currently offline'
    }
  }), []);

  // Store messages in a ref to avoid dependency changes
  const messagesRef = React.useRef(messages);
  
  // Update the ref when language changes
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  useEffect(() => {
    // التحقق من وجود تحديثات للـ Service Worker
    const checkForUpdates = async () => {
      const hasUpdate = await checkForServiceWorkerUpdate();
      if (hasUpdate) {
        setUpdateAvailable(true);
        setUpdateMessage(language === 'ar' ? messagesRef.current.update.ar : messagesRef.current.update.en);
      }
    };
    
    // التحقق من التحديثات عند تحميل المكون
    checkForUpdates();
    
    // التحقق من التحديثات كل 60 دقيقة
    const updateCheckInterval = setInterval(checkForUpdates, 60 * 60 * 1000);
    
    // الاستماع لرسائل Service Worker
    const removeMessageListener = setupServiceWorkerMessages((message) => {
      if (message.type === 'SW_UPDATED') {
        setUpdateAvailable(true);
        setUpdateMessage(language === 'ar' ? messagesRef.current.update.ar : messagesRef.current.update.en);
      } else if (message.type === 'OFFLINE_MODE') {
        setNetworkStatus({
          show: true,
          isOnline: false,
          message: language === 'ar' ? messagesRef.current.offline.ar : messagesRef.current.offline.en,
        });
      } else if (message.type === 'ONLINE_MODE') {
        setNetworkStatus({
          show: true,
          isOnline: true,
          message: language === 'ar' ? messagesRef.current.online.ar : messagesRef.current.online.en,
        });
        
        // إخفاء إشعار الاتصال بعد 3 ثوانٍ
        setTimeout(() => {
          setNetworkStatus((prev) => ({ ...prev, show: false }));
        }, 3000);
      }
    });

    // التحقق من حالة الاتصال الحالية
    setNetworkStatus((prev) => ({
      ...prev,
      isOnline: navigator.onLine,
    }));

    // تنظيف مستمعي الأحداث
    return () => {
      removeMessageListener();
      clearInterval(updateCheckInterval);
    };
  }, [language]); // Only depend on language, not messages

  // تطبيق التحديث
  const applyUpdate = async () => {
    const updated = await applyServiceWorkerUpdate();
    if (updated) {
      setUpdateAvailable(false);
      // إعادة تحميل الصفحة لتطبيق التحديثات
      window.location.reload();
    }
  };

  return (
    <>
      {/* إشعار تحديث Service Worker */}
      <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 90, sm: 30 } }} // تعديل الموضع على الأجهزة المحمولة
      >
        <UpdateNotification>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RefreshIcon sx={{ mr: 1 }} />
            <Typography variant="body2">
            {messagesRef.current.update[language]}
          </Typography>
        </Box>
        <UpdateButton
          variant="outlined"
          size="small"
          onClick={applyUpdate}
        >
          {messagesRef.current.updateAction[language]}
          </UpdateButton>
        </UpdateNotification>
      </Snackbar>

      {/* إشعار حالة الاتصال */}
      <Snackbar
        open={networkStatus.show}
        autoHideDuration={networkStatus.isOnline ? 3000 : null} // إخفاء تلقائي فقط عند استعادة الاتصال
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setNetworkStatus((prev) => ({ ...prev, show: false }))}
      >
        <Box
          sx={{
            borderRadius: 1,
            padding: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: 400,
            boxShadow: 3,
            ...(networkStatus.isOnline
              ? { bgcolor: 'success.main', color: 'success.contrastText' }
              : { bgcolor: 'warning.dark', color: 'warning.contrastText' }),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {networkStatus.isOnline ? (
              <WifiIcon sx={{ mr: 1 }} />
            ) : (
              <WifiOffIcon sx={{ mr: 1 }} />
            )}
            <Typography variant="body2">
              {networkStatus.isOnline 
                ? messagesRef.current.online[language] 
                : messagesRef.current.offline[language]}
            </Typography>
          </Box>
          {!networkStatus.isOnline && (
            <Button 
              size="small" 
              variant="outlined" 
              color="inherit"
              onClick={() => {
                // محاولة إعادة الاتصال
                window.location.reload();
              }}
            >
              <RefreshIcon fontSize="small" />
            </Button>
          )}
        </Box>
      </Snackbar>
    </>
  );
};

export default ServiceWorkerUpdater;